<template>
  <div class="p-6 space-y-4 animate-fade-in">
    <h2 class="text-white text-xl font-semibold">My Equipment</h2>
    <p class="text-slate-400 text-sm">Your checked out and reserved equipment</p>

    <div class="flex gap-2 text-xs">
      <button :class="tabClass('checked')" @click="activeTab = 'checked'">
        Checked Out ({{ checked.length }})
      </button>
      <button :class="tabClass('reserved')" @click="activeTab = 'reserved'">
        Reserved ({{ reserved.length }})
      </button>
    </div>

    <div v-if="error" class="text-red-400 text-sm">{{ error }}</div>

    <div v-if="activeTab === 'checked'" class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div v-for="r in checked" :key="r.id" class="panel-neo panel-neo--glassier p-4">
        <div class="flex items-center justify-between">
          <div>
            <div class="text-white font-semibold">{{ r.displayName }}</div>
            <div class="font-mono text-xs text-slate-300">{{ r.equipment_serial }}</div>
          </div>
          <span
            class="px-2 py-0.5 rounded bg-blue-500/15 border border-blue-500/30 text-blue-300 text-xs"
            >Checked Out</span
          >
        </div>
        <div class="text-slate-400 text-xs mt-2">{{ r.organization_name || '—' }}</div>
        <div class="text-slate-400 text-xs">
          {{ r.contact_name }}<span v-if="r.contact_title">, {{ r.contact_title }}</span>
        </div>
        <div class="text-slate-300 text-xs mt-1" v-if="addr(r)">{{ addr(r) }}</div>
        <div class="text-slate-400 text-xs mt-2">{{ fmt(r.start_at) }} → {{ fmt(r.end_at) }}</div>
        <div class="mt-2 flex items-center justify-between text-xs">
          <div class="text-slate-300">
            TestDrive Roadmap Status:
            <span class="text-white font-medium">{{ roadmapStatusLabel(r) }}</span>
          </div>
          <button class="text-primary-400 underline" @click="openRoadmap(r)">View Roadmap</button>
        </div>

        <!-- Shipment tracking info -->
        <div v-if="r._shipments && r._shipments.length" class="mt-3 space-y-1.5 border-t border-slate-700/50 pt-2">
          <div v-for="sh in r._shipments" :key="sh.id" class="flex items-center justify-between text-xs">
            <div class="flex items-center gap-2">
              <span :class="sh.type === 'outbound' ? 'text-cyan-400' : 'text-amber-400'" class="font-medium uppercase">
                {{ sh.type === 'outbound' ? '→ Out' : '← Ret' }}
              </span>
              <span class="text-slate-400">{{ sh.carrier }}</span>
              <span class="font-mono text-slate-200">{{ sh.tracking_number || '—' }}</span>
            </div>
            <span class="text-slate-500">{{ sh.status }}</span>
          </div>
        </div>

        <div class="mt-3 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <a
              v-if="addr(r)"
              :href="map(r)"
              target="_blank"
              class="text-primary-400 text-xs underline"
              >Map</a
            >
          </div>
          <div class="flex items-center gap-2">
            <a
              v-if="primaryLabelUrl(r)"
              :href="primaryLabelUrl(r)"
              target="_blank"
              rel="noopener noreferrer"
              class="px-2 py-1 text-[11px] rounded bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 inline-flex items-center gap-1"
            >
              View
            </a>
            <button
              v-if="primaryLabelUrl(r)"
              class="px-2 py-1 text-[11px] rounded bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700"
              @click="printLabel(r)"
            >
              Print
            </button>
            <a
              v-if="primaryLabelUrl(r)"
              :href="primaryLabelUrl(r)"
              download
              class="px-2 py-1 text-[11px] rounded bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700"
            >
              Download
            </a>
            <button
              class="px-2 py-1 text-[11px] rounded bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 inline-flex items-center gap-1"
              @click="openNotifications(r)"
            >
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Alerts
            </button>
            <button
              v-if="r._shipments && r._shipments.length"
              class="px-2 py-1 text-[11px] rounded bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700"
              @click="copyTracking(r)"
            >
              Copy Tracking
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div v-for="r in reserved" :key="r.id" class="panel-neo panel-neo--glassier p-4">
        <div class="flex items-center justify-between">
          <div>
            <div class="text-white font-semibold">{{ r.displayName }}</div>
            <div class="font-mono text-xs text-slate-300">{{ r.equipment_serial }}</div>
          </div>
          <span
            class="px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs"
            >Reserved</span
          >
        </div>
        <div class="text-slate-400 text-xs mt-2">{{ r.organization_name || '—' }}</div>
        <div class="text-slate-400 text-xs">
          {{ r.contact_name }}<span v-if="r.contact_title">, {{ r.contact_title }}</span>
        </div>
        <div class="text-slate-300 text-xs mt-1" v-if="addr(r)">{{ addr(r) }}</div>
        <div class="text-slate-400 text-xs mt-2">{{ fmt(r.start_at) }} → {{ fmt(r.end_at) }}</div>
        <div class="mt-2 flex items-center justify-between text-xs">
          <div class="text-slate-300">
            TestDrive Roadmap Status:
            <span class="text-white font-medium">{{ roadmapStatusLabel(r) }}</span>
          </div>
          <button class="text-primary-400 underline" @click="openRoadmap(r)">View Roadmap</button>
        </div>

        <!-- Shipment tracking info (if labels were created for a reserved item) -->
        <div v-if="r._shipments && r._shipments.length" class="mt-3 space-y-1.5 border-t border-slate-700/50 pt-2">
          <div v-for="sh in r._shipments" :key="sh.id" class="flex items-center justify-between text-xs">
            <div class="flex items-center gap-2">
              <span :class="sh.type === 'outbound' ? 'text-cyan-400' : 'text-amber-400'" class="font-medium uppercase">
                {{ sh.type === 'outbound' ? '→ Out' : '← Ret' }}
              </span>
              <span class="text-slate-400">{{ sh.carrier }}</span>
              <span class="font-mono text-slate-200">{{ sh.tracking_number || '—' }}</span>
            </div>
            <span class="text-slate-500">{{ sh.status }}</span>
          </div>
        </div>

        <div class="mt-3 text-right">
          <div class="flex items-center justify-end gap-2">
            <a
              v-if="primaryLabelUrl(r)"
              :href="primaryLabelUrl(r)"
              target="_blank"
              rel="noopener noreferrer"
              class="px-2 py-1 text-[11px] rounded bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700"
            >
              View
            </a>
            <button
              v-if="primaryLabelUrl(r)"
              class="px-2 py-1 text-[11px] rounded bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700"
              @click="printLabel(r)"
            >
              Print
            </button>
            <a
              v-if="primaryLabelUrl(r)"
              :href="primaryLabelUrl(r)"
              download
              class="px-2 py-1 text-[11px] rounded bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700"
            >
              Download
            </a>
            <a
              v-if="addr(r)"
              :href="map(r)"
              target="_blank"
              class="text-primary-400 text-xs underline"
              >Map</a
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Checkout Notifications Slide-out Panel -->
    <CheckoutNotificationsPanel
      :visible="notifPanelOpen"
      :checkout-id="notifPanelCheckoutId"
      :checkout-label="notifPanelLabel"
      :sender-name="auth0Store.displayName ?? undefined"
      @close="notifPanelOpen = false"
    />

    <RoadmapModal
      v-if="roadmapOpen && roadmapItem"
      :make="roadmapItem.make || 'Equipment'"
      :model="roadmapItem.model || ''"
      :serial="roadmapItem.equipment_serial || roadmapItem.serial || 'N/A'"
      :status="roadmapStatus(roadmapItem)"
      :has-return-label="hasReturnLabel(roadmapItem)"
      @close="roadmapOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { supabase } from '@/lib/supabaseClient'
import CheckoutNotificationsPanel from '@/components/CheckoutNotificationsPanel.vue'
import RoadmapModal from '@/components/modals/RoadmapModal.vue'
import { useAuth0Store } from '@/stores/auth0'
import { isActiveCheckoutStatus, isMockSerial, roadmapStatusFromSources } from '@/lib/checkoutStatus'

const auth0Store = useAuth0Store()
const error = ref('')
const checked = ref<any[]>([])
const reserved = ref<any[]>([])
const activeTab = ref<'checked' | 'reserved'>('checked')

/** Slide-out notification panel state. */
const notifPanelOpen = ref(false)
const notifPanelCheckoutId = ref<number | null>(null)
const notifPanelLabel = ref('')
const roadmapOpen = ref(false)
const roadmapItem = ref<any | null>(null)

/** Map equipment_serial_id → checkout_id for items loaded from reservations. */
const serialToCheckoutId = ref<Record<number, number>>({})
const labelUrlResolveCache = new Map<string, Promise<string>>()

/** Open the notifications slide-out for a checkout item. */
async function openNotifications(item: any) {
  // Determine the checkout_id from various sources
  let coId: number | null = null

  // Source 2 items have 'co-{id}' formatted ids
  if (typeof item.id === 'string' && item.id.startsWith('co-')) {
    coId = parseInt(item.id.replace('co-', ''), 10)
  }

  // Direct checkout_id property (if available)
  if (!coId && item.checkout_id) {
    coId = item.checkout_id
  }

  // Try cached serial → checkout mapping
  if (!coId && item.equipment_serial_id && serialToCheckoutId.value[item.equipment_serial_id]) {
    coId = serialToCheckoutId.value[item.equipment_serial_id]
  }

  // Look up checkout_id from equipment_serial_id as fallback
  if (!coId && item.equipment_serial_id) {
    try {
      const { data: coRow } = await supabase
        .schema('app')
        .from('checkouts')
        .select('id')
        .eq('equipment_serial_id', item.equipment_serial_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (coRow) {
        coId = coRow.id
        serialToCheckoutId.value[item.equipment_serial_id] = coRow.id
      }
    } catch { /* ignore */ }
  }

  if (!coId) return

  notifPanelCheckoutId.value = coId
  notifPanelLabel.value = [item.displayName, item.equipment_serial].filter(Boolean).join(' — ')
  notifPanelOpen.value = true
}

function tabClass(k: string) {
  const base = 'px-2 py-1 rounded border border-slate-700'
  return activeTab.value === k
    ? base + ' bg-slate-200 text-black'
    : base + ' bg-slate-900/70 text-slate-200 hover:bg-slate-800'
}

function addr(r: any) {
  const a = [r.address1, r.city, r.state, r.zip].filter(Boolean).join(', ')
  return a || ''
}
function map(r: any) {
  return addr(r) ? `https://maps.google.com/?q=${encodeURIComponent(addr(r))}` : '#'
}
function primaryLabelUrl(r: any): string {
  const shipments = Array.isArray(r?._shipments) ? r._shipments : []
  const outbound = shipments.find((s: any) => s?.type === 'outbound' && s?.label_url)
  const anyWithLabel = shipments.find((s: any) => s?.label_url)
  return String(outbound?.label_url || anyWithLabel?.label_url || '')
}

function parseLabelPathFromSignedUrl(rawUrl: string): string | null {
  const url = String(rawUrl || '').trim()
  if (!url) return null
  const match = url.match(/\/storage\/v1\/object\/sign\/labels\/([^?]+)/i)
  if (!match?.[1]) return null
  try {
    return decodeURIComponent(match[1])
  } catch {
    return match[1]
  }
}

async function resolveLabelUrl(rawUrl: string): Promise<string> {
  const url = String(rawUrl || '').trim()
  if (!url) return ''
  if (!url.includes('/storage/v1/object/sign/labels/')) return url

  const cached = labelUrlResolveCache.get(url)
  if (cached) return cached

  const request = (async () => {
    const path = parseLabelPathFromSignedUrl(url)
    if (!path) return url
    try {
      const { data, error } = await supabase.functions.invoke('shipping-label-url', {
        body: { path },
      })
      if (error) return url
      const next = String((data as any)?.url || '').trim()
      return next || url
    } catch {
      return url
    }
  })()

  labelUrlResolveCache.set(url, request)
  return request
}
function fmt(s: string | null | undefined) {
  if (!s) return '—'
  return new Date(s).toLocaleString()
}

function hasReturnLabel(item: any): boolean {
  return Boolean((item?._shipments || []).some((s: any) => s?.type === 'return'))
}

function roadmapStatus(item: any) {
  return roadmapStatusFromSources(item?.status, item?._shipments || [])
}

function roadmapStatusLabel(item: any) {
  return roadmapStatus(item)
    .split('_')
    .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function openRoadmap(item: any) {
  roadmapItem.value = item
  roadmapOpen.value = true
}

/**
 * Load equipment items from TWO sources:
 *   1. reservations_v — date-based reservations (checked-out / reserved)
 *   2. app.checkouts + app.shipments — any checkout with active shipments
 *
 * Items from source 2 appear in the "Checked Out" tab even if the original
 * reservation date range has expired, ensuring equipment with purchased
 * labels always shows up with tracking info.
 */
async function load() {
  try {
    // ── Source 1: Reservations ──────────────────────────────────────
    const { data, error: err } = await supabase.from('reservations_v').select('*')
    if (err) throw err
    const resRows = (data || []).map(normalizeReservation)

    // Build a lookup: equipment_serial_id → reservation row (most recent)
    const resBySerial: Record<number, any> = {}
    for (const r of resRows) {
      if (r.equipment_serial_id) resBySerial[r.equipment_serial_id] = r
    }

    // ── Source 2: Checkouts + Shipments ─────────────────────────────
    // Fetch canonical checkout-state rows (recent first)
    const { data: coData } = await supabase
      .schema('app')
      .from('checkout_state_v')
      .select(
        'checkout_id,equipment_serial_id,equipment_serial,checkout_status,is_active_checkout,is_reportable_active_checkout,roadmap_status,created_at,contact_id,organization_id,outbound_legs,has_outbound_in_transit,has_return_in_transit',
      )
      .order('checkout_id', { ascending: false })
    const checkoutRows: any[] = (coData || []).filter(
      (co: any) =>
        Boolean(co?.is_reportable_active_checkout) &&
        isActiveCheckoutStatus(co.checkout_status),
    )

    // Fetch all shipments (recent first)
    let allShipments: any[] = []
    try {
      const { data: shipData } = await supabase
        .schema('app')
        .from('shipments')
        .select('id, type, carrier, service, tracking_number, label_url, status, checkout_id')
        .order('created_at', { ascending: false })
      allShipments = (shipData || []) as any[]
      const labelResolutionTasks = allShipments.map(async (sh) => {
        const currentLabelUrl = String(sh?.label_url || '')
        if (!currentLabelUrl) return sh
        const refreshed = await resolveLabelUrl(currentLabelUrl)
        return { ...sh, label_url: refreshed }
      })
      allShipments = await Promise.all(labelResolutionTasks)
    } catch { /* non-fatal */ }

    // Map checkout_id → shipments[]
    const shipByCheckout: Record<number, any[]> = {}
    for (const sh of allShipments) {
      if (sh.checkout_id) {
        if (!shipByCheckout[sh.checkout_id]) shipByCheckout[sh.checkout_id] = []
        shipByCheckout[sh.checkout_id].push(sh)
      }
    }

    // Map equipment_serial_id → shipments[] (via checkout linkage)
    const shipBySerial: Record<number, any[]> = {}
    for (const co of checkoutRows) {
      if (co.equipment_serial_id && shipByCheckout[co.checkout_id]) {
        if (!shipBySerial[co.equipment_serial_id]) shipBySerial[co.equipment_serial_id] = []
        shipBySerial[co.equipment_serial_id].push(...shipByCheckout[co.checkout_id])
      }
    }

    // Load serial metadata in one round-trip for checkout-only rows.
    const serialIds = Array.from(
      new Set(
        checkoutRows
          .map((co: any) => Number(co.equipment_serial_id))
          .filter((id: number) => Number.isFinite(id)),
      ),
    )
    const serialInfoById: Record<number, any> = {}
    if (serialIds.length) {
      try {
        const { data: serialRows } = await supabase
          .from('equipment_serials_v')
          .select('id, serial, make, model, equipment_type_name')
          .in('id', serialIds)
        ;(serialRows || []).forEach((row: any) => {
          serialInfoById[Number(row.id)] = row
        })
      } catch {
        // Non-fatal: fallback to reservation info only.
      }
    }

    const contactIds = Array.from(
      new Set(
        checkoutRows
          .map((co: any) => Number(co.contact_id))
          .filter((id: number) => Number.isFinite(id)),
      ),
    )
    const contactById: Record<number, any> = {}
    if (contactIds.length) {
      try {
        const { data: contacts } = await supabase
          .schema('app')
          .from('contacts')
          .select('id, name, title, email, phone, address1, city, state, zip')
          .in('id', contactIds)
        ;(contacts || []).forEach((row: any) => {
          contactById[Number(row.id)] = row
        })
      } catch {
        // Non-fatal.
      }
    }

    const orgIds = Array.from(
      new Set(
        checkoutRows
          .map((co: any) => Number(co.organization_id))
          .filter((id: number) => Number.isFinite(id)),
      ),
    )
    const orgById: Record<number, any> = {}
    if (orgIds.length) {
      try {
        const { data: orgRows } = await supabase
          .schema('app')
          .from('organizations')
          .select('id, name')
          .in('id', orgIds)
        ;(orgRows || []).forEach((row: any) => {
          orgById[Number(row.id)] = row
        })
      } catch {
        // Non-fatal.
      }
    }

    // ── Merge: enrich reservation rows with shipments ──────────────
    for (const row of resRows) {
      row._shipments = shipBySerial[row.equipment_serial_id] || []
    }

    // ── Build checkout rows for items that have active shipments
    //    but whose reservations are expired ("past") ────────────────
    const checkoutItems: any[] = []
    const seenSerials = new Set<number>()

    for (const co of checkoutRows) {
      if (!co.equipment_serial_id) continue
      if (!isActiveCheckoutStatus(co.checkout_status)) continue
      if (seenSerials.has(co.equipment_serial_id)) continue // already shown from reservations
      const shipments = shipByCheckout[co.checkout_id] || []

      // Look up equipment info from serials view or from the past reservation
      const resInfo = resBySerial[co.equipment_serial_id]
      const serialInfo = serialInfoById[Number(co.equipment_serial_id)] || resInfo

      if (!serialInfo) continue

      const contact = contactById[Number(co.contact_id)] || {}
      const organization = orgById[Number(co.organization_id)] || {}
      const serialValue = serialInfo.equipment_serial || serialInfo.serial
      const displayName = [serialInfo.make, serialInfo.model].filter(Boolean).join(' ')
      if (isMockSerial(serialValue)) continue

      checkoutItems.push({
        id: `co-${co.checkout_id}`,
        equipment_serial_id: co.equipment_serial_id,
        equipment_serial: serialValue,
        displayName,
        make: serialInfo.make || '',
        model: serialInfo.model || '',
        organization_name: serialInfo.organization_name || organization.name || '',
        contact_name: serialInfo.contact_name || contact.name || '',
        contact_title: serialInfo.contact_title || contact.title || '',
        address1: serialInfo.address1 || serialInfo.contact_address1 || contact.address1 || '',
        city: serialInfo.city || serialInfo.contact_city || contact.city || '',
        state: serialInfo.state || serialInfo.contact_state || contact.state || '',
        zip: serialInfo.zip || serialInfo.contact_zip || contact.zip || '',
        start_at: co.created_at,
        end_at: null,
        status: String(co.roadmap_status || co.checkout_status || 'checked-out').replace(/_/g, '-'),
        _shipments: shipments,
      })
      seenSerials.add(co.equipment_serial_id)
    }

    // ── Final lists ────────────────────────────────────────────────
    const activeReserved = resRows.filter((r: any) => r.status === 'reserved')
    const activeChecked = checkoutItems

    checked.value = activeChecked
    reserved.value = activeReserved
  } catch (e: any) {
    error.value = e?.message || String(e)
  }
}

/**
 * Copy all tracking numbers for a reservation item to clipboard.
 */
function copyTracking(r: any) {
  const nums = (r._shipments || [])
    .map((s: any) => s.tracking_number)
    .filter(Boolean)
    .join('\n')
  if (nums) {
    navigator.clipboard.writeText(nums).catch(() => {})
  }
}

function printLabel(r: any) {
  const url = primaryLabelUrl(r)
  if (!url) return
  try {
    const w = window.open(url, '_blank')
    if (!w) return
    const onload = () => {
      try {
        w.focus()
        w.print()
      } catch {
        // Non-fatal.
      }
    }
    if (w.document?.readyState === 'complete') onload()
    else w.addEventListener('load', onload)
  } catch {
    // Non-fatal.
  }
}

onMounted(load)

function normalizeReservation(row: any) {
  const parts = [row.make, row.model]
  const base = parts.filter(Boolean).join(' ')
  const descriptor = row.descriptor ? ` — ${row.descriptor}` : ''
  return {
    ...row,
    displayName: (base || row.equipment_name || '').concat(descriptor),
  }
}
</script>

<style scoped></style>
